<script setup>
/**
 * Renders the events of one section (resolved server-side into `data.events`)
 * as a count-aware grid of cards, each linking to its own detail page. A lone
 * event renders wide; the presentational card is the shared EventCardBlock.
 * Full listings (no `limit` — the /events and /retrospect pages) get a date
 * sorter; curated home sections keep the portal order untouched.
 */
import EventCardBlock from "~/components/blocks/EventCardBlock.vue";
import ListSortSelect from "~/components/ListSortSelect.vue";
import { sortByDate } from "~/utils/dates";

const { t } = useI18n();
const localePath = useLocalePath();

const props = defineProps({ data: { type: Object, required: true } });

const sortMode = ref("default");
const showSorter = computed(
  () => !props.data.limit && (props.data.events || []).length > 1,
);
const events = computed(() =>
  sortByDate(props.data.events, sortMode.value, "start_date"),
);

// Map a server event summary onto the presentational card's `data` shape
// (date_display → date, cover_image → image, excerpt → description).
function toCard(ev) {
  return {
    date: ev.date_display,
    title: ev.title,
    image: ev.cover_image,
    location: ev.location,
    club: ev.club,
    description: ev.excerpt,
  };
}
</script>

<template>
  <section class="section">
    <div class="container-x">
      <!-- Optional section heading + "view all" link -->
      <div v-if="data.heading" class="mb-10 md:mb-12 flex flex-wrap items-end justify-between gap-4">
        <div class="space-y-3">
          <p v-if="data.eyebrow" class="text-xs tracking-[0.3em] uppercase text-brand-green font-semibold">{{ data.eyebrow }}</p>
          <h2 class="font-display text-3xl md:text-4xl">{{ data.heading }}</h2>
          <div class="rainbow-rule"></div>
        </div>
        <NuxtLink
          v-if="data.view_all_href"
          :to="localePath(data.view_all_href)"
          class="no-underline inline-flex items-center gap-1.5 text-sm font-medium text-brand-green hover:text-brand-orange"
        >
          {{ data.view_all_label || t("home.events.viewAll") }}
          <span aria-hidden="true">→</span>
        </NuxtLink>
      </div>

      <!-- Date sorter — full listings only (default = curated portal order) -->
      <div v-if="showSorter" class="mb-6 flex justify-end">
        <ListSortSelect v-model="sortMode" />
      </div>

      <!-- Lone event: wide featured card -->
      <NuxtLink
        v-if="events.length === 1"
        :to="localePath(events[0].url)"
        class="block no-underline"
      >
        <EventCardBlock :data="toCard(events[0])" featured />
      </NuxtLink>

      <!-- Multiple events: count-aware grid -->
      <div
        v-else-if="events.length"
        class="grid gap-6"
        :class="events.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'"
      >
        <NuxtLink
          v-for="ev in events"
          :key="ev.id"
          :to="localePath(ev.url)"
          class="block no-underline"
        >
          <EventCardBlock :data="toCard(ev)" />
        </NuxtLink>
      </div>

      <!-- Empty section -->
      <p v-else class="text-center text-muted">{{ t("event.empty") }}</p>
    </div>
  </section>
</template>
